<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[880px]"
    @close="emit('cancel')"
  >
    <template #header>
      <div class="flex flex-col gap-1 text-left">
        <h2 class="text-xl font-bold text-textPrimary">Confirmar sobrescrita</h2>
        <p class="text-sm text-neutral50" data-testid="leg-overwrite-subtitle">{{ subtitle }}</p>
      </div>
    </template>

    <div class="flex flex-col gap-4 text-left">
      <p
        v-if="staleNotice"
        class="rounded border border-primary bg-primary5 px-3 py-2 text-sm text-textPrimary"
        role="status"
        data-testid="leg-overwrite-stale-notice"
      >
        Os registros deste plantão mudaram — revise as escolhas antes de confirmar.
      </p>

      <article
        v-for="conflict in conflicts"
        :key="conflict.doctorStationId"
        class="rounded border"
        :class="conflict.frozen ? 'border-negative bg-red-50' : 'border-gray-30 bg-white'"
        :data-testid="`leg-overwrite-station-${conflict.doctorStationId}`"
      >
        <header class="flex flex-wrap items-center gap-2 border-b border-gray-30 px-3 py-2 text-sm">
          <span class="font-bold">{{ conflict.doctorName }}</span>
          <span class="text-gray-500">
            {{ conflict.stationLabel }} · {{ toDDMMYYYY(conflict.stationDate) }}
          </span>
          <LabelBadge
            v-if="conflict.priorityPayment"
            :label="priorityLabel(conflict)"
            color="orange"
            size="small"
            :icon="null"
          />
          <LabelBadge
            v-if="conflict.frozen"
            label="Pós-plantão aprovado"
            color="red"
            size="small"
            :icon="null"
          />
        </header>

        <p
          v-if="conflict.frozen"
          class="px-3 pt-2 text-sm font-semibold text-negative"
          :data-testid="`leg-overwrite-frozen-${conflict.doctorStationId}`"
        >
          A sobrescrita está bloqueada: o pós-plantão deste plantão já foi aprovado. Reabra o
          pós-plantão e aprove este registro de novo.
        </p>

        <!-- Abaixo de sm (390px) cada perna vira um bloco empilhado: a tabela de 4 colunas
             estourava o card e cortava "Manter atual". -->
        <table class="block w-full text-sm sm:table">
          <thead class="hidden sm:table-header-group">
            <tr class="text-left text-xs uppercase text-gray-500">
              <th class="px-3 py-2 font-bold">Perna</th>
              <th class="px-3 py-2 font-bold">Registro atual</th>
              <th class="px-3 py-2 font-bold">Este registro</th>
              <th class="px-3 py-2 font-bold">Qual vale</th>
            </tr>
          </thead>
          <tbody class="block sm:table-row-group">
            <tr
              v-for="leg in conflict.legs"
              :key="leg.leg"
              class="grid grid-cols-2 border-t border-gray-30 align-top sm:table-row"
              :class="leg.timeChanges ? 'bg-yellow-50' : ''"
              :data-testid="`leg-overwrite-row-${conflict.doctorStationId}-${leg.leg}`"
            >
              <td class="col-span-2 px-3 pb-0 pt-2 font-semibold sm:table-cell sm:py-2">
                {{ LEG_LABELS[leg.leg] }}
              </td>
              <td class="min-w-0 px-3 py-2 sm:table-cell">
                <span class="block text-xs uppercase text-gray-500 sm:hidden">Registro atual</span>
                <div class="flex flex-col">
                  <span>{{ formatLegTime(leg.current.time, conflict.timezone) }}</span>
                  <span class="text-xs text-gray-500">
                    {{ RECORD_KIND_LABELS[leg.current.kind] ?? leg.current.kind }} #{{
                      leg.current.containerId
                    }}<template v-if="leg.current.pinned"> · perna fixada</template>
                  </span>
                </div>
              </td>
              <td class="min-w-0 px-3 py-2 sm:table-cell">
                <span class="block text-xs uppercase text-gray-500 sm:hidden">Este registro</span>
                <span :class="leg.timeChanges ? 'font-bold text-amber-800' : ''">
                  {{ formatLegTime(leg.incoming.time, conflict.timezone) }}
                </span>
                <span
                  v-if="leg.timeChanges"
                  class="block text-xs font-semibold text-amber-800"
                  :data-testid="`leg-overwrite-time-changes-${conflict.doctorStationId}-${leg.leg}`"
                  >horário diferente</span
                >
                <span v-else class="block text-xs text-gray-500">mesmo horário</span>
              </td>
              <td class="col-span-2 px-3 pb-3 pt-0 sm:table-cell sm:py-2">
                <div
                  class="flex w-full overflow-hidden rounded border border-primary30 sm:inline-flex sm:w-auto"
                  role="group"
                  :aria-label="`Qual registro vale no ${LEG_LABELS[leg.leg]}`"
                >
                  <button
                    v-for="option in CHOICE_OPTIONS"
                    :key="option.value"
                    type="button"
                    class="flex-1 whitespace-nowrap px-3 py-2 text-xs font-semibold sm:flex-none sm:py-1 disabled:cursor-not-allowed disabled:opacity-50"
                    :class="
                      currentChoice(conflict, leg.leg) === option.value
                        ? 'bg-primary30 text-white'
                        : 'bg-white text-primary30'
                    "
                    :aria-pressed="currentChoice(conflict, leg.leg) === option.value"
                    :disabled="conflict.frozen || submitting"
                    :data-testid="`leg-overwrite-${conflict.doctorStationId}-${leg.leg}-${option.testId}-button`"
                    @click="choose(conflict, leg.leg, option.value)"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="conflict.priorityPayment"
          class="flex flex-col gap-1 border-t border-dashed border-alert bg-orange-50 px-3 py-2 text-sm"
          :data-testid="`leg-overwrite-priority-${conflict.doctorStationId}`"
        >
          <span class="font-semibold text-orange-800">
            Plantão com pagamento prioritário: {{ priorityPaymentSummary(conflict) }}.
          </span>
          <span
            v-if="valueStatusOf(conflict) === 'PENDING'"
            class="italic text-gray-600"
            :data-testid="`leg-overwrite-value-pending-${conflict.doctorStationId}`"
          >
            Recalculando valor…
          </span>
          <span
            v-else-if="valueStatusOf(conflict) === 'ERROR'"
            class="font-semibold text-orange-800"
            :data-testid="`leg-overwrite-value-error-${conflict.doctorStationId}`"
          >
            Não foi possível recalcular o valor desta combinação.
          </span>
          <span v-else-if="effectiveValueSummary(effectiveValueOf(conflict))">
            {{ effectiveValueSummary(effectiveValueOf(conflict)) }}
          </span>
          <span class="text-xs text-gray-500">
            Confira o valor antes de confirmar: a sobrescrita pode mudar o que entra no pagamento.
          </span>
        </div>

        <p
          v-if="hasInvalidLegCombination(conflict, decisions[conflict.doctorStationId])"
          class="border-t border-gray-30 px-3 py-2 text-sm text-negative"
          :data-testid="`leg-overwrite-invalid-${conflict.doctorStationId}`"
        >
          Com essa combinação o check-out fica antes do check-in. Escolha outra combinação.
        </p>
      </article>

      <div v-if="justificationRequired" class="flex flex-col gap-1">
        <label for="leg-overwrite-justification" class="text-sm font-semibold text-textPrimary">
          Justificativa da sobrescrita (obrigatória)
        </label>
        <span class="text-xs text-gray-500">
          Obrigatória porque algum horário muda com a sobrescrita.
        </span>
        <textarea
          id="leg-overwrite-justification"
          v-model="justification"
          rows="3"
          class="rounded border border-gray-30 p-2 text-sm"
          :disabled="submitting"
          data-testid="leg-overwrite-justification-input"
        />
      </div>

      <p
        v-if="errorMessage"
        class="text-sm text-negative"
        role="alert"
        data-testid="leg-overwrite-error"
      >
        {{ errorMessage }}
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <OutlineButton
          text="Cancelar"
          width="110px"
          height="40px"
          :disabled="submitting"
          data-testid="leg-overwrite-cancel-button"
          @click="emit('cancel')"
        />
        <FilledButton
          text="Confirmar sobrescrita e aprovar"
          class="bg-primary30"
          width="280px"
          height="40px"
          :disabled="!canConfirm || submitting"
          data-testid="leg-overwrite-confirm-button"
          @click="confirm"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
/**
 * Spec 2026-09-24 §8.1 — "Confirmar sobrescrita". Componente burro: recebe os conflitos da
 * prévia (ou do 409 de corrida), emite o corpo `legOverwrites` e avisa (`decisionsChange`)
 * quando as decisões mudam num plantão com pagamento prioritário — o composable rechama a
 * prévia e devolve o valor exato em `effectiveValues` (D-W5). Regras em `utils/leg-overwrite.ts`.
 */
import { computed, ref, watch } from 'vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import type { DSAttendanceLegOverwritePayload } from '@/Network/Types/Requests/DsAttendanceRequest'
import type { EffectiveValueStatus } from '../composables/useLegOverwriteApproval'
import type {
  EffectiveValue,
  LegKey,
  LegOverwriteChoice,
  LegOverwriteConflict
} from '@/Network/Types/Responses/DsAttendanceLegOverwriteResponses'
import { toDDMMYYYY } from '../utils/activity-time'
import {
  LEG_LABELS,
  PRIORITY_PAYMENT_LABELS,
  RECORD_KIND_LABELS,
  buildDefaultDecisions,
  buildLegOverwritesPayload,
  canConfirmLegOverwrite,
  choiceOf,
  effectiveValueSummary,
  formatLegTime,
  hasInvalidLegCombination,
  priorityPaymentSummary,
  requiresAnyJustification,
  type DecisionsByStation
} from '../utils/leg-overwrite'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    conflicts: LegOverwriteConflict[]
    /** Valor recalculado para as decisões atuais, por `doctorStationId` (D-W5). */
    effectiveValues?: Record<number, EffectiveValue | null>
    /** Recálculo em curso (PENDING) ou que falhou (ERROR), por `doctorStationId`. */
    effectiveValueStatus?: Record<number, EffectiveValueStatus>
    submitting?: boolean
    errorMessage?: string | null
    staleNotice?: boolean
  }>(),
  {
    effectiveValues: () => ({}),
    effectiveValueStatus: () => ({}),
    submitting: false,
    errorMessage: null,
    staleNotice: false
  }
)

const emit = defineEmits<{
  confirm: [legOverwrites: DSAttendanceLegOverwritePayload[]]
  decisionsChange: [legOverwrites: DSAttendanceLegOverwritePayload[]]
  cancel: []
}>()

const CHOICE_OPTIONS: Array<{ value: LegOverwriteChoice; label: string; testId: string }> = [
  { value: 'NEW', label: 'Este registro', testId: 'new' },
  { value: 'KEEP', label: 'Manter atual', testId: 'keep' }
]

const decisions = ref<DecisionsByStation>({})
const justification = ref('')

// D-W2: conflitos novos (abertura ou 409 de corrida) voltam ao default; a justificativa fica.
watch(
  () => props.conflicts,
  (conflicts) => {
    decisions.value = buildDefaultDecisions(conflicts)
  },
  { immediate: true }
)

// Cada abertura é uma aprovação nova: nada da anterior fica digitado.
watch(
  () => props.modelValue,
  (open, wasOpen) => {
    if (open && !wasOpen) justification.value = ''
  }
)

const hasPriorityPayment = computed(() =>
  props.conflicts.some((conflict) => conflict.priorityPayment)
)

function currentChoice(conflict: LegOverwriteConflict, leg: LegKey): LegOverwriteChoice {
  return choiceOf(decisions.value[conflict.doctorStationId], leg)
}

function choose(conflict: LegOverwriteConflict, leg: LegKey, choice: LegOverwriteChoice) {
  if (currentChoice(conflict, leg) === choice) return
  decisions.value = {
    ...decisions.value,
    [conflict.doctorStationId]: { ...decisions.value[conflict.doctorStationId], [leg]: choice }
  }
  // D-W5: só o plantão com pagamento prioritário tem valor a recalcular.
  if (hasPriorityPayment.value) {
    emit('decisionsChange', buildLegOverwritesPayload(props.conflicts, decisions.value, ''))
  }
}

function effectiveValueOf(conflict: LegOverwriteConflict): EffectiveValue | null {
  return props.effectiveValues[conflict.doctorStationId] ?? conflict.effectiveValue
}

function valueStatusOf(conflict: LegOverwriteConflict): EffectiveValueStatus | undefined {
  return props.effectiveValueStatus[conflict.doctorStationId]
}

function priorityLabel(conflict: LegOverwriteConflict): string {
  const type = conflict.priorityPayment?.type
  return (type && PRIORITY_PAYMENT_LABELS[type]) || 'Pagamento prioritário'
}

const subtitle = computed(() => {
  const count = props.conflicts.length
  const lead =
    count === 1
      ? '1 plantão deste registro já tem outro registro aprovado cobrindo a mesma perna.'
      : `${count} plantões deste registro já têm outro registro aprovado cobrindo a mesma perna.`
  return `${lead} Escolha, perna a perna, qual registro vale.`
})

const justificationRequired = computed(() =>
  requiresAnyJustification(props.conflicts, decisions.value)
)

const canConfirm = computed(() =>
  canConfirmLegOverwrite(props.conflicts, decisions.value, justification.value)
)

function confirm() {
  if (!canConfirm.value || props.submitting) return
  emit('confirm', buildLegOverwritesPayload(props.conflicts, decisions.value, justification.value))
}
</script>
